/**
 * PopupManager Component
 * Admin interface for managing popup notifications with rich content support
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Plus, Edit, Trash2, Eye, EyeOff, Users, Shield, 
  Info, AlertTriangle, CheckCircle, Gift, Calendar,
  UserCheck, RefreshCw, BarChart3, MousePointer, X,
  Image, Link, ExternalLink, Search, Check
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { 
  Dialog, DialogContent, DialogDescription, 
  DialogHeader, DialogTitle 
} from './ui/dialog';
import { Switch } from './ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { API } from '../App';

const POPUP_TYPES = [
  { value: 'info', label: 'Information', icon: Info, color: 'bg-blue-100 text-blue-700', border: 'border-blue-200' },
  { value: 'warning', label: 'Avertissement', icon: AlertTriangle, color: 'bg-amber-100 text-amber-700', border: 'border-amber-200' },
  { value: 'success', label: 'Succès', icon: CheckCircle, color: 'bg-green-100 text-green-700', border: 'border-green-200' },
  { value: 'promotion', label: 'Promotion', icon: Gift, color: 'bg-purple-100 text-purple-700', border: 'border-purple-200' }
];

const TARGET_AUDIENCES = [
  { value: 'client', label: 'Clients', icon: Users, description: 'Acheteurs et vendeurs' },
  { value: 'staff', label: 'Employés', icon: Shield, description: 'Admins et super admins' },
  { value: 'all', label: 'Tout le monde', icon: Users, description: 'Tous les utilisateurs' }
];

const PopupManager = () => {
  const [popups, setPopups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingPopup, setEditingPopup] = useState(null);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState('list');
  const [userSearch, setUserSearch] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    popup_type: 'info',
    target_audience: 'client',
    is_active: true,
    priority: 1,
    show_once: true,
    start_date: '',
    end_date: '',
    assigned_users: [],
    image_url: '',
    action_button_text: '',
    action_button_url: '',
    secondary_button_text: '',
    secondary_button_url: ''
  });

  useEffect(() => {
    fetchPopups();
    fetchUsers();
    fetchStats();
  }, []);

  const fetchPopups = async () => {
    try {
      const response = await axios.get(`${API}/super-admin/popups`);
      setPopups(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des popups');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/super-admin/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/super-admin/popups/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      popup_type: 'info',
      target_audience: 'client',
      is_active: true,
      priority: 1,
      show_once: true,
      start_date: '',
      end_date: '',
      assigned_users: [],
      image_url: '',
      action_button_text: '',
      action_button_url: '',
      secondary_button_text: '',
      secondary_button_url: ''
    });
    setEditingPopup(null);
    setShowPreview(false);
  };

  const openCreateDialog = () => {
    resetForm();
    setShowDialog(true);
  };

  const openEditDialog = (popup) => {
    setEditingPopup(popup);
    setFormData({
      title: popup.title,
      content: popup.content,
      popup_type: popup.popup_type,
      target_audience: popup.target_audience,
      is_active: popup.is_active,
      priority: popup.priority,
      show_once: popup.show_once,
      start_date: popup.start_date || '',
      end_date: popup.end_date || '',
      assigned_users: popup.assigned_users || [],
      image_url: popup.image_url || '',
      action_button_text: popup.action_button_text || '',
      action_button_url: popup.action_button_url || '',
      secondary_button_text: popup.secondary_button_text || '',
      secondary_button_url: popup.secondary_button_url || ''
    });
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingPopup) {
        await axios.put(`${API}/super-admin/popups/${editingPopup.id}`, formData);
        toast.success('Popup modifié avec succès');
      } else {
        await axios.post(`${API}/super-admin/popups`, formData);
        toast.success('Popup créé avec succès');
      }
      
      setShowDialog(false);
      resetForm();
      fetchPopups();
      fetchStats();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const toggleActive = async (popup) => {
    try {
      await axios.put(`${API}/super-admin/popups/${popup.id}`, { 
        is_active: !popup.is_active 
      });
      fetchPopups();
      toast.success(popup.is_active ? 'Popup désactivé' : 'Popup activé');
    } catch (error) {
      toast.error('Erreur lors de la modification');
    }
  };

  const deletePopup = async (popup) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce popup?')) return;
    
    try {
      await axios.delete(`${API}/super-admin/popups/${popup.id}`);
      toast.success('Popup supprimé');
      fetchPopups();
      fetchStats();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const getTypeConfig = (type) => {
    return POPUP_TYPES.find(t => t.value === type) || POPUP_TYPES[0];
  };

  const getAudienceConfig = (audience) => {
    return TARGET_AUDIENCES.find(a => a.value === audience) || TARGET_AUDIENCES[0];
  };

  const toggleUserSelection = (email) => {
    setFormData(prev => ({
      ...prev,
      assigned_users: prev.assigned_users.includes(email)
        ? prev.assigned_users.filter(e => e !== email)
        : [...prev.assigned_users, email]
    }));
  };

  const filteredUsers = users.filter(user => 
    user.email.toLowerCase().includes(userSearch.toLowerCase()) ||
    (user.name && user.name.toLowerCase().includes(userSearch.toLowerCase()))
  );

  // Preview Component
  const PopupPreview = () => {
    const typeConfig = getTypeConfig(formData.popup_type);
    const IconComponent = typeConfig.icon;
    
    return (
      <div className="border-2 border-dashed border-gray-300 rounded-xl p-4 bg-gray-50">
        <p className="text-xs text-gray-500 mb-3 text-center">Aperçu du popup</p>
        <div className={`relative w-full max-w-sm mx-auto ${typeConfig.color} ${typeConfig.border} border-2 rounded-xl shadow-lg`}>
          <button className="absolute top-3 right-3 p-1 rounded-full hover:bg-gray-200/50">
            <X className="h-4 w-4 text-gray-500" />
          </button>
          
          <div className="flex items-center gap-3 p-4 pb-2">
            <div className={`p-2 rounded-full ${typeConfig.color} ${typeConfig.border} border`}>
              <IconComponent className="h-5 w-5" />
            </div>
            <h3 className="font-bold text-base text-gray-900 pr-8">
              {formData.title || 'Titre du popup'}
            </h3>
          </div>
          
          {formData.image_url && (
            <div className="px-4 pb-2">
              <img 
                src={formData.image_url} 
                alt="Preview" 
                className="w-full h-32 object-cover rounded-lg"
                onError={(e) => e.target.style.display = 'none'}
              />
            </div>
          )}
          
          <div className="px-4 pb-4">
            <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap">
              {formData.content || 'Contenu du message...'}
            </p>
          </div>
          
          {(formData.action_button_text || formData.secondary_button_text) && (
            <div className="flex gap-2 px-4 pb-4">
              {formData.action_button_text && (
                <button className="flex-1 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg font-medium">
                  {formData.action_button_text}
                </button>
              )}
              {formData.secondary_button_text && (
                <button className="flex-1 px-3 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg font-medium">
                  {formData.secondary_button_text}
                </button>
              )}
            </div>
          )}
          
          <div className="flex items-center justify-end px-4 py-3 bg-white/50 rounded-b-xl border-t border-gray-200/50">
            <button className="px-4 py-1.5 bg-gray-900 hover:bg-gray-800 text-white text-sm rounded-lg">
              Compris
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h3 className="text-lg font-semibold">Gestion des popups</h3>
          <p className="text-sm text-gray-500">
            Créez des notifications personnalisées pour vos clients et employés
          </p>
        </div>
        <Button onClick={openCreateDialog} className="bg-brand-600 hover:bg-brand-700" data-testid="create-popup-btn">
          <Plus className="h-4 w-4 mr-2" />
          Nouveau popup
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="list" data-testid="popups-list-tab">
            <Eye className="h-4 w-4 mr-2" />
            Liste ({popups.length})
          </TabsTrigger>
          <TabsTrigger value="stats" data-testid="popups-stats-tab">
            <BarChart3 className="h-4 w-4 mr-2" />
            Statistiques
          </TabsTrigger>
        </TabsList>

        {/* List Tab */}
        <TabsContent value="list" className="space-y-4 mt-4">
          {/* Stats Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card className="p-3">
              <div className="text-center">
                <p className="text-2xl font-bold">{popups.length}</p>
                <p className="text-xs text-gray-500">Total</p>
              </div>
            </Card>
            <Card className="p-3">
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">
                  {popups.filter(p => p.is_active).length}
                </p>
                <p className="text-xs text-gray-500">Actifs</p>
              </div>
            </Card>
            <Card className="p-3">
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-600">
                  {popups.filter(p => p.target_audience === 'client').length}
                </p>
                <p className="text-xs text-gray-500">Clients</p>
              </div>
            </Card>
            <Card className="p-3">
              <div className="text-center">
                <p className="text-2xl font-bold text-purple-600">
                  {popups.filter(p => p.target_audience === 'staff').length}
                </p>
                <p className="text-xs text-gray-500">Staff</p>
              </div>
            </Card>
          </div>

          {/* Popup List */}
          <div className="space-y-3">
            {popups.length === 0 ? (
              <Card className="p-8 text-center">
                <Info className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">Aucun popup créé</p>
                <Button 
                  onClick={openCreateDialog} 
                  variant="outline" 
                  className="mt-4"
                >
                  Créer votre premier popup
                </Button>
              </Card>
            ) : (
              popups.map((popup) => {
                const typeConfig = getTypeConfig(popup.popup_type);
                const audienceConfig = getAudienceConfig(popup.target_audience);
                const TypeIcon = typeConfig.icon;
                const AudienceIcon = audienceConfig.icon;
                
                return (
                  <Card key={popup.id} className={`${!popup.is_active ? 'opacity-60' : ''}`} data-testid={`popup-card-${popup.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-2">
                            <Badge className={typeConfig.color}>
                              <TypeIcon className="h-3 w-3 mr-1" />
                              {typeConfig.label}
                            </Badge>
                            <Badge variant="outline">
                              <AudienceIcon className="h-3 w-3 mr-1" />
                              {audienceConfig.label}
                            </Badge>
                            {!popup.is_active && (
                              <Badge variant="secondary">Inactif</Badge>
                            )}
                            {popup.assigned_users?.length > 0 && (
                              <Badge variant="outline" className="bg-amber-50">
                                <UserCheck className="h-3 w-3 mr-1" />
                                {popup.assigned_users.length} user(s)
                              </Badge>
                            )}
                            {popup.image_url && (
                              <Badge variant="outline" className="bg-pink-50">
                                <Image className="h-3 w-3 mr-1" />
                                Image
                              </Badge>
                            )}
                            {popup.action_button_text && (
                              <Badge variant="outline" className="bg-cyan-50">
                                <Link className="h-3 w-3 mr-1" />
                                Bouton
                              </Badge>
                            )}
                          </div>
                          <h4 className="font-semibold text-gray-900 truncate">
                            {popup.title}
                          </h4>
                          <p className="text-sm text-gray-500 line-clamp-2 mt-1">
                            {popup.content}
                          </p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                            {(popup.start_date || popup.end_date) && (
                              <div className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                {popup.start_date && <span>Du {popup.start_date}</span>}
                                {popup.end_date && <span>au {popup.end_date}</span>}
                              </div>
                            )}
                            <div className="flex items-center gap-1">
                              <Eye className="h-3 w-3" />
                              {popup.view_count || 0} vues
                            </div>
                            <div className="flex items-center gap-1">
                              <MousePointer className="h-3 w-3" />
                              {popup.click_count || 0} clics
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleActive(popup)}
                            title={popup.is_active ? 'Désactiver' : 'Activer'}
                            data-testid={`toggle-popup-${popup.id}`}
                          >
                            {popup.is_active ? (
                              <Eye className="h-4 w-4 text-green-600" />
                            ) : (
                              <EyeOff className="h-4 w-4 text-gray-400" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(popup)}
                            data-testid={`edit-popup-${popup.id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deletePopup(popup)}
                            className="text-red-600 hover:text-red-700"
                            data-testid={`delete-popup-${popup.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        </TabsContent>

        {/* Stats Tab */}
        <TabsContent value="stats" className="space-y-4 mt-4">
          {stats ? (
            <>
              {/* Overview Stats */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <Card className="p-3">
                  <div className="text-center">
                    <p className="text-2xl font-bold">{stats.total_popups}</p>
                    <p className="text-xs text-gray-500">Total popups</p>
                  </div>
                </Card>
                <Card className="p-3">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-green-600">{stats.active_popups}</p>
                    <p className="text-xs text-gray-500">Actifs</p>
                  </div>
                </Card>
                <Card className="p-3">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-blue-600">{stats.total_views}</p>
                    <p className="text-xs text-gray-500">Vues totales</p>
                  </div>
                </Card>
                <Card className="p-3">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-purple-600">{stats.total_clicks}</p>
                    <p className="text-xs text-gray-500">Clics totaux</p>
                  </div>
                </Card>
                <Card className="p-3">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-amber-600">{stats.total_dismissals}</p>
                    <p className="text-xs text-gray-500">Fermetures</p>
                  </div>
                </Card>
                <Card className="p-3">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-brand-600">{stats.average_ctr}%</p>
                    <p className="text-xs text-gray-500">CTR moyen</p>
                  </div>
                </Card>
              </div>

              {/* Top Popups */}
              {stats.top_popups && stats.top_popups.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Top Popups par engagement</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {stats.top_popups.map((popup, index) => (
                        <div key={popup.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <span className="text-lg font-bold text-gray-300">#{index + 1}</span>
                            <div>
                              <p className="font-medium text-sm">{popup.title}</p>
                              <div className="flex items-center gap-3 text-xs text-gray-500">
                                <span><Eye className="h-3 w-3 inline mr-1" />{popup.views} vues</span>
                                <span><MousePointer className="h-3 w-3 inline mr-1" />{popup.clicks} clics</span>
                              </div>
                            </div>
                          </div>
                          <Badge className={popup.ctr > 5 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                            {popup.ctr}% CTR
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <BarChart3 className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>Chargement des statistiques...</p>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingPopup ? 'Modifier le popup' : 'Créer un popup'}
            </DialogTitle>
            <DialogDescription>
              Ce popup sera affiché aux utilisateurs lors de leur connexion
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Basic Info */}
              <div>
                <Label>Titre *</Label>
                <Input
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Ex: Bienvenue sur la plateforme!"
                  data-testid="popup-title-input"
                />
              </div>

              <div>
                <Label>Contenu *</Label>
                <textarea
                  required
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="Le message à afficher..."
                  className="w-full min-h-[80px] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 text-sm"
                  data-testid="popup-content-input"
                />
              </div>

              {/* Type and Audience */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Type</Label>
                  <select
                    value={formData.popup_type}
                    onChange={(e) => setFormData({ ...formData, popup_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    data-testid="popup-type-select"
                  >
                    {POPUP_TYPES.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label>Audience</Label>
                  <select
                    value={formData.target_audience}
                    onChange={(e) => setFormData({ ...formData, target_audience: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    data-testid="popup-audience-select"
                  >
                    {TARGET_AUDIENCES.map(audience => (
                      <option key={audience.value} value={audience.value}>{audience.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Image URL */}
              <div>
                <Label className="flex items-center gap-2">
                  <Image className="h-4 w-4" />
                  Image (URL)
                </Label>
                <Input
                  type="url"
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  placeholder="https://example.com/image.jpg"
                  data-testid="popup-image-input"
                />
              </div>

              {/* Action Buttons */}
              <div className="space-y-3 p-3 bg-gray-50 rounded-lg">
                <Label className="flex items-center gap-2 text-sm font-medium">
                  <ExternalLink className="h-4 w-4" />
                  Boutons d'action (optionnel)
                </Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    value={formData.action_button_text}
                    onChange={(e) => setFormData({ ...formData, action_button_text: e.target.value })}
                    placeholder="Texte bouton principal"
                    className="text-sm"
                    data-testid="popup-action-btn-text"
                  />
                  <Input
                    type="url"
                    value={formData.action_button_url}
                    onChange={(e) => setFormData({ ...formData, action_button_url: e.target.value })}
                    placeholder="URL du bouton"
                    className="text-sm"
                    data-testid="popup-action-btn-url"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    value={formData.secondary_button_text}
                    onChange={(e) => setFormData({ ...formData, secondary_button_text: e.target.value })}
                    placeholder="Texte bouton secondaire"
                    className="text-sm"
                    data-testid="popup-secondary-btn-text"
                  />
                  <Input
                    type="url"
                    value={formData.secondary_button_url}
                    onChange={(e) => setFormData({ ...formData, secondary_button_url: e.target.value })}
                    placeholder="URL du bouton"
                    className="text-sm"
                    data-testid="popup-secondary-btn-url"
                  />
                </div>
              </div>

              {/* Date Range */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Date début</Label>
                  <Input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    data-testid="popup-start-date"
                  />
                </div>
                <div>
                  <Label>Date fin</Label>
                  <Input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    data-testid="popup-end-date"
                  />
                </div>
              </div>

              {/* Priority */}
              <div>
                <Label>Priorité (1-10)</Label>
                <Input
                  type="number"
                  min="1"
                  max="10"
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 1 })}
                  data-testid="popup-priority-input"
                />
                <p className="text-xs text-gray-500 mt-1">Plus élevée = affiché en premier</p>
              </div>

              {/* User Assignment */}
              <div>
                <Label className="flex items-center gap-2">
                  <UserCheck className="h-4 w-4" />
                  Utilisateurs spécifiques (optionnel)
                </Label>
                <div className="border rounded-lg max-h-40 overflow-y-auto mt-2">
                  <div className="sticky top-0 bg-white p-2 border-b">
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        value={userSearch}
                        onChange={(e) => setUserSearch(e.target.value)}
                        placeholder="Rechercher..."
                        className="pl-8 h-8 text-sm"
                      />
                    </div>
                  </div>
                  <div className="p-2 space-y-1">
                    {filteredUsers.slice(0, 20).map(user => (
                      <label
                        key={user.id}
                        className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-gray-50 ${
                          formData.assigned_users.includes(user.email) ? 'bg-brand-50' : ''
                        }`}
                      >
                        <div className={`w-4 h-4 border rounded flex items-center justify-center ${
                          formData.assigned_users.includes(user.email) 
                            ? 'bg-brand-600 border-brand-600' 
                            : 'border-gray-300'
                        }`}>
                          {formData.assigned_users.includes(user.email) && (
                            <Check className="h-3 w-3 text-white" />
                          )}
                        </div>
                        <input
                          type="checkbox"
                          checked={formData.assigned_users.includes(user.email)}
                          onChange={() => toggleUserSelection(user.email)}
                          className="sr-only"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{user.name || user.email}</p>
                          <p className="text-xs text-gray-500 truncate">{user.email}</p>
                        </div>
                        <Badge variant="outline" className="text-xs shrink-0">{user.role}</Badge>
                      </label>
                    ))}
                  </div>
                </div>
                {formData.assigned_users.length > 0 && (
                  <p className="text-xs text-brand-600 mt-1">
                    {formData.assigned_users.length} utilisateur(s) sélectionné(s)
                  </p>
                )}
              </div>

              {/* Switches */}
              <div className="flex items-center justify-between gap-4 p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.show_once}
                    onCheckedChange={(checked) => setFormData({ ...formData, show_once: checked })}
                    data-testid="popup-show-once-switch"
                  />
                  <Label className="cursor-pointer text-sm">Une seule fois</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                    data-testid="popup-is-active-switch"
                  />
                  <Label className="cursor-pointer text-sm">Actif</Label>
                </div>
              </div>

              {/* Submit Buttons */}
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                  Annuler
                </Button>
                <Button type="submit" className="bg-brand-600 hover:bg-brand-700" data-testid="popup-submit-btn">
                  {editingPopup ? 'Sauvegarder' : 'Créer'}
                </Button>
              </div>
            </form>

            {/* Preview */}
            <div className="hidden lg:block">
              <PopupPreview />
            </div>
          </div>

          {/* Mobile Preview Toggle */}
          <div className="lg:hidden mt-4">
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => setShowPreview(!showPreview)}
            >
              <Eye className="h-4 w-4 mr-2" />
              {showPreview ? 'Masquer' : 'Voir'} l'aperçu
            </Button>
            {showPreview && (
              <div className="mt-4">
                <PopupPreview />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PopupManager;
